import { useMemo } from 'react';
import type { NodeProps } from '@xyflow/react';
import { cn } from '@/lib/utils';
import { useCustomToolBuilder } from '../../builder/builder-context';
import { outputPaths } from '../../output-paths';
import { stepFields } from '../../steps/step-fields';
import type { StepNodeData } from '../flow-layout';
import { CARD, FOCUSED } from './card';
import { StepArgs } from './step-args';
import { StepHeader } from './step-header';
import { StepOutputs } from './step-outputs';

/** One step: its tool, the arguments it takes (wired in, or set in place), and the result it passes on. */
export const StepNode = ({ data }: NodeProps) => {
	const { stepId } = data as StepNodeData;
	const { draft, focus, stepTools, fieldsOf } = useCustomToolBuilder();
	const index = draft.steps.findIndex((step) => step.id === stepId);
	const step = draft.steps[index];
	const tool = step ? stepTools.toolFor(step) : undefined;
	const paths = useMemo(() => outputPaths(tool?.outputSchema), [tool?.outputSchema]);
	if (!step) return null;
	const fields = fieldsOf(step) ?? [];
	const missing = fields.some((field) => field.required && !step.args[field.name]);
	const focused = focus?.kind === 'step' && focus.id === stepId;

	return (
		<div className={cn(CARD, focused ? FOCUSED : missing ? 'border-red-300' : 'border-gray-300')}>
			<StepHeader step={step} index={index} tool={tool} />
			<StepArgs step={step} fields={stepFields(step, fields)} loading={!tool && stepTools.isLoading} />
			<StepOutputs step={step} paths={paths} />
		</div>
	);
};
